import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { productionDashboardAPI } from '@/utils/api';
import { Package, AlertTriangle, TrendingUp, Factory, ArrowRight, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

const ProductionDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await productionDashboardAPI.getStats(user.id || 'demo-user');
      setDashboard(res.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton-shimmer h-64 rounded-lg"></div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight">Gestion de Production</h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          Vue d'ensemble de votre production cosmétique artisanale
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="hover:border-primary/20 hover:shadow-md transition-all duration-200">
          <CardHeader>
            <CardTitle className="text-2xl">{stats.total_materials || 0}</CardTitle>
            <CardDescription>Matières premières</CardDescription>
          </CardHeader>
        </Card>

        <Card className="hover:border-primary/20 hover:shadow-md transition-all duration-200">
          <CardHeader>
            <CardTitle className="text-2xl">{stats.total_products || 0}</CardTitle>
            <CardDescription>Produits finis</CardDescription>
          </CardHeader>
        </Card>

        <Card className="hover:border-primary/20 hover:shadow-md transition-all duration-200">
          <CardHeader>
            <CardTitle className="text-2xl">{stats.total_production_volume_30d || 0}</CardTitle>
            <CardDescription>Lots produits (30j)</CardDescription>
          </CardHeader>
        </Card>

        <Card className="hover:border-primary/20 hover:shadow-md transition-all duration-200 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-2xl text-amber-600">{stats.low_stock_count || 0}</CardTitle>
            <CardDescription>Alertes stock bas</CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Button
          data-testid="btn-goto-materials"
          onClick={() => navigate('/production/materials')}
          className="h-20 gap-3 text-base"
          variant="outline"
        >
          <Package size={24} />
          Matières Premières
          <ArrowRight size={18} />
        </Button>

        <Button
          data-testid="btn-goto-products"
          onClick={() => navigate('/production/products')}
          className="h-20 gap-3 text-base"
          variant="outline"
        >
          <Factory size={24} />
          Produits Finis
          <ArrowRight size={18} />
        </Button>

        <Button
          data-testid="btn-goto-journal"
          onClick={() => navigate('/production/journal')}
          className="h-20 gap-3 text-base"
          variant="outline"
        >
          <TrendingUp size={24} />
          Journal de Production
          <ArrowRight size={18} />
        </Button>

        <Button
          data-testid="btn-goto-planning"
          onClick={() => navigate('/production/planning')}
          className="h-20 gap-3 text-base border-accent/20 text-accent hover:bg-accent/5"
          variant="outline"
        >
          <ShoppingCart size={24} />
          Planification & Achats
          <ArrowRight size={18} />
        </Button>
      </div>

      {/* Top Profitable Products */}
      {dashboard?.top_profitable_products?.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Top 5 Produits les Plus Rentables</CardTitle>
            <CardDescription>Classés par marge brute publique</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboard.top_profitable_products.map((product, index) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between p-4 border border-border rounded-md hover:border-primary/20 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-semibold text-primary">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{product.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Coût: {product.total_cost.toFixed(2)}€ • Prix: {product.sale_price_public_ttc.toFixed(2)}€
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-green-600">+{product.gross_margin_public.toFixed(2)}€</p>
                    <p className="text-sm text-muted-foreground">Coef: {product.coefficient_public.toFixed(2)}x</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Production */}
      {dashboard?.recent_productions?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Productions Récentes</CardTitle>
            <CardDescription>10 dernières entrées du journal</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dashboard.recent_productions.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between p-3 border border-border rounded-md"
                >
                  <div>
                    <p className="font-medium text-foreground">{entry.product_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(entry.date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{entry.quantity_lots} lots</p>
                    <p className="text-sm text-muted-foreground">{entry.total_production_cost.toFixed(2)}€</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductionDashboard;
